import { useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

import { ChartBarIcon } from '@gto/icons-react/outline/chart-bar'
import { ClockIcon } from '@gto/icons-react/outline/clock'
import { PlusIcon } from '@gto/icons-react/outline/plus'
import { TrashIcon } from '@gto/icons-react/outline/trash'
import { UserGroupIcon } from '@gto/icons-react/outline/user-group'
import { UsersIcon } from '@gto/icons-react/outline/users'
import { Button } from '@gto/ui/button'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { Typography } from '@gto/ui/typography'

import type { AdminAffiliateTierSchema } from '@/api/gen'
import {
  getListTiersQueryKey,
  useCreateTier,
  useDeleteTier,
  useListTiers,
  useUpdateTier,
} from '@/api/gen'
import { AppDialog } from '@/components/app-dialog'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { DataTable } from '@/components/data-table'
import { EmptyState } from '@/components/empty-state'
import { TextareaField, TextField } from '@/components/form'
import { InlineQueryError } from '@/components/inline-query-error'
import { MutationError } from '@/components/mutation-error'
import { Page } from '@/components/page'
import { RowActionsMenu } from '@/components/row-actions-menu'
import { StatsCard } from '@/components/stats-card'
import { fixedColumn, fluidColumn } from '@/components/table-columns'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { parsePercent } from '@/utils/parse-percent'
import { toRate } from '@/utils/to-rate'

const DEFAULT_LOCK_DAYS = 14

// ─── Domain type + boundary coercion ────────────────────────────────────────
// The affiliate BE serialises the Decimal `commission_rate` as a STRING on the
// wire (e.g. "15.00"). `Tier` is what every consumer (KPIs, columns, the form)
// receives: a real `number`. `toTier` is the single boundary — passed to the
// query `select` — so no call site does math on a raw wire string (the class of
// bug that crashed settlements with `.toFixed is not a function`).

type Tier = Omit<AdminAffiliateTierSchema, 'commission_rate'> & {
  commission_rate: number
}

const toTier = (wire: AdminAffiliateTierSchema): Tier => ({
  ...wire,
  commission_rate: toRate(wire.commission_rate, 'tiers toRate: non-numeric wire commission_rate'),
})

const lockDaysOf = (config: AdminAffiliateTierSchema['config']): number => {
  const days = config.payout_frequency_days

  return typeof days === 'number' && Number.isFinite(days) ? days : DEFAULT_LOCK_DAYS
}

// ─── Tier Columns ───────────────────────────────────────────────────────────

const useTierColumns = (
  onDelete: (tier: Tier) => void,
  onViewPartners: (tier: Tier) => void,
): ColumnDef<Tier>[] => {
  const { formatMessage } = useIntl()

  return createColumns<Tier>([
    {
      accessorKey: 'name',
      header: formatMessage({ id: 'admin.tiers.name' }),
      ...fluidColumn({ min: 120, flex: 1 }),
      enableSorting: false,
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: 'commission_rate',
      header: formatMessage({ id: 'admin.tiers.rate' }),
      ...fluidColumn({ min: 80, flex: 0.6, align: 'right' }),
      cell: ({ row }) => (
        <Typography
          color="primary"
          variant="body2"
        >
          <FormattedNumber
            maximumFractionDigits={2}
            style="percent"
            value={row.original.commission_rate / 100}
          />
        </Typography>
      ),
    },
    {
      accessorKey: 'config',
      header: formatMessage({ id: 'admin.tiers.lockPeriod' }),
      ...fluidColumn({ min: 80, flex: 0.6, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => (
        <Typography
          color="text.secondary"
          variant="body2"
        >
          {formatMessage(
            { id: 'admin.tiers.lockPeriodDays' },
            { days: lockDaysOf(row.original.config) },
          )}
        </Typography>
      ),
    },
    {
      accessorKey: 'partner_count',
      header: formatMessage({ id: 'admin.tiers.partners' }),
      ...fluidColumn({ min: 70, flex: 0.5, align: 'right' }),
      cell: ({ getValue }) => String(getValue<number | null>() ?? 0),
    },
    {
      accessorKey: 'description',
      header: formatMessage({ id: 'admin.tiers.description' }),
      ...fluidColumn({ min: 140, flex: 1.4 }),
      enableSorting: false,
      cell: ({ getValue }) => (
        <Typography
          color="text.secondary"
          variant="body2"
        >
          {getValue<string | null>() ?? '—'}
        </Typography>
      ),
    },
    {
      id: 'actions',
      header: '',
      ...fixedColumn({ min: 48, align: 'center' }),
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <RowActionsMenu
          actions={[
            {
              label: <FormattedMessage id="admin.tiers.viewPartners" />,
              icon: (
                <UserGroupIcon
                  height={16}
                  width={16}
                />
              ),
              // Cross-nav into the partners table prefiltered by tier. The
              // partners page is fully URL-driven (validateSearch), so a
              // search-only navigate is enough — no event payload, no parallel
              // state to seed.
              onClick: () => onViewPartners(row.original),
            },
            {
              label: <FormattedMessage id="admin.common.delete" />,
              icon: (
                <TrashIcon
                  height={16}
                  width={16}
                />
              ),
              color: 'error.main',
              disabled: (row.original.partner_count ?? 0) > 0,
              onClick: () => onDelete(row.original),
            },
          ]}
        />
      ),
    },
  ])
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export const TiersPage = () => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Tier | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Tier | null>(null)

  const tiersQuery = useListTiers({ query: { select: (data) => data.map(toTier) } })
  const { data: tiers, isLoading, isError, refetch } = tiersQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  // Reports on TERMINAL failure so retries don't multiply Sentry events.
  useReportQueryError(tiersQuery, 'useListTiers failed: ')

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: getListTiersQueryKey() })

  // A failed delete must reach Sentry AND surface inside the confirm dialog, and
  // must NOT close the dialog (the success-only side-effect).
  const deleteError = useMutationErrorHandler('useDeleteTier failed: ')

  const deleteTier = useDeleteTier({
    mutation: {
      onSuccess: () => {
        invalidate()
        setDeleteTarget(null)
      },
      onError: deleteError.onError,
    },
  })

  const openDelete = (tier: Tier) => {
    deleteError.clearError()
    setDeleteTarget(tier)
  }

  const viewPartners = (tier: Tier) => {
    void navigate({
      to: '/admin/partners',
      search: { tier: tier.id },
    })
  }

  const columns = useTierColumns(openDelete, viewPartners)
  const rows = tiers ?? []

  const totalPartners = rows.reduce((sum, tier) => sum + (tier.partner_count ?? 0), 0)
  const topRate = rows.reduce((max, tier) => Math.max(max, tier.commission_rate), 0)

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage id="admin.tiers.title" />}
        actions={
          <Button
            disabled={isError}
            variant="contained"
            startIcon={
              <PlusIcon
                height={18}
                width={18}
              />
            }
            onClick={() => setCreateOpen(true)}
          >
            <FormattedMessage id="admin.tiers.create" />
          </Button>
        }
      />

      <Page.Content>
        {isError ? (
          <InlineQueryError
            data-tst="tiers-error"
            messageId="admin.tiers.error"
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            <div
              className="flex flex-wrap gap-2"
              data-tst="kpi-strip"
            >
              <DashboardKpiTile
                icon={<ChartBarIcon />}
                label={<FormattedMessage id="admin.tiers.kpi.count" />}
                loading={isLoading}
                tooltip={formatMessage({ id: 'tooltip.tiers.count' })}
                value={<FormattedNumber value={rows.length} />}
              />
              <DashboardKpiTile
                icon={<ClockIcon />}
                label={<FormattedMessage id="admin.tiers.kpi.topRate" />}
                loading={isLoading}
                tooltip={formatMessage({ id: 'tooltip.tiers.topRate' })}
                value={
                  <FormattedNumber
                    maximumFractionDigits={2}
                    style="percent"
                    value={topRate / 100}
                  />
                }
              />
              <DashboardKpiTile
                icon={<UsersIcon />}
                label={<FormattedMessage id="admin.tiers.kpi.partners" />}
                loading={isLoading}
                tooltip={formatMessage({ id: 'tooltip.tiers.partners' })}
                value={<FormattedNumber value={totalPartners} />}
              />
            </div>

            <StatsCard
              icon={<ChartBarIcon />}
              title={<FormattedMessage id="admin.tiers.listTitle" />}
            >
              {!isLoading && rows.length === 0 ? (
                <EmptyState title={<FormattedMessage id="admin.tiers.empty" />} />
              ) : (
                <DataTable<Tier>
                  columns={columns}
                  data={rows}
                  getRowId={(row) => String(row.id)}
                  loading={isLoading}
                  onRowClick={(row) => setEditTarget(row)}
                />
              )}
            </StatsCard>
          </>
        )}
      </Page.Content>

      <TierFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      {editTarget && (
        <TierFormDialog
          open
          tier={editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          open
          confirmColor="error"
          confirmLabel={<FormattedMessage id="admin.common.delete" />}
          loading={deleteTier.isPending}
          title={<FormattedMessage id="admin.tiers.deleteTitle" />}
          description={formatMessage(
            { id: 'admin.tiers.deleteDescription' },
            { name: deleteTarget.name },
          )}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => deleteTier.mutate({ tierId: deleteTarget.id })}
        >
          <MutationError
            data-tst="tier-delete-error"
            messageId="admin.tiers.deleteError"
            show={deleteError.hasError}
          />
        </ConfirmDialog>
      )}
    </Page>
  )
}

// ─── Create/Edit Tier Dialog ─────────────────────────────────────────────────

type TierFormDialogProps = {
  open: boolean
  onClose: () => void
  tier?: Tier
}

const TierFormDialog = ({ open, onClose, tier }: TierFormDialogProps) => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const isEdit = tier != null

  const [name, setName] = useState(tier?.name ?? '')
  const [rate, setRate] = useState(() => (tier ? String(tier.commission_rate) : ''))
  const [rateInvalid, setRateInvalid] = useState(false)
  const [lockDays, setLockDays] = useState(() =>
    tier ? String(lockDaysOf(tier.config)) : String(DEFAULT_LOCK_DAYS),
  )
  const [description, setDescription] = useState(tier?.description ?? '')

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: getListTiersQueryKey() })

  // A failed create/update must reach Sentry AND surface inside the form dialog,
  // and must NOT close the dialog or invalidate (the success-only side-effects).
  const saveError = useMutationErrorHandler(
    isEdit ? 'useUpdateTier failed: ' : 'useCreateTier failed: ',
  )

  const createTier = useCreateTier({
    mutation: {
      onSuccess: () => {
        invalidate()
        onClose()
      },
      onError: saveError.onError,
    },
  })
  const updateTier = useUpdateTier({
    mutation: {
      onSuccess: () => {
        invalidate()
        onClose()
      },
      onError: saveError.onError,
    },
  })

  const isPending = createTier.isPending || updateTier.isPending

  const handleSubmit = () => {
    saveError.clearError()
    // Rate is required (0–100). Save is `onClick`, not a form submit, so the
    // `<TextField required>` browser gate does NOT block it; without this guard
    // `Number('abc')` → NaN would serialise as JSON `null` and either crash the
    // BE or wipe the rate silently, and empty input would write `0`.
    const parsedRate = parsePercent(rate, { allowEmpty: false })
    if (!parsedRate.ok || parsedRate.value == null) {
      setRateInvalid(true)

      return
    }
    setRateInvalid(false)
    const config = { payout_frequency_days: Number(lockDays) || DEFAULT_LOCK_DAYS }

    if (isEdit) {
      updateTier.mutate({
        tierId: tier.id,
        data: { name, commission_rate: parsedRate.value, config, description },
      })
    } else {
      createTier.mutate({
        data: {
          name,
          commission_rate: parsedRate.value,
          config,
          description: description === '' ? undefined : description,
        },
      })
    }
  }

  return (
    <AppDialog
      open={open}
      width={420}
      onClose={onClose}
    >
      <AppDialog.Header
        title={
          isEdit ? (
            <FormattedMessage id="admin.tiers.editTitle" />
          ) : (
            <FormattedMessage id="admin.tiers.create" />
          )
        }
      />
      <AppDialog.Body>
        <MutationError
          data-tst="tier-save-error"
          messageId={isEdit ? 'admin.tiers.updateError' : 'admin.tiers.createError'}
          show={saveError.hasError}
        />
        <TextField
          required
          label={formatMessage({ id: 'admin.tiers.name' })}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          required
          data-tst="tier-rate"
          error={rateInvalid ? formatMessage({ id: 'admin.tiers.rate.invalid' }) : undefined}
          helperText={formatMessage({ id: 'admin.tiers.rateHelper' })}
          label={formatMessage({ id: 'admin.tiers.rate' })}
          max={100}
          min={0}
          step={0.01}
          type="number"
          value={rate}
          onChange={(e) => {
            setRate(e.target.value)
            if (rateInvalid) {
              setRateInvalid(false)
            }
          }}
        />
        <TextField
          helperText={formatMessage({ id: 'admin.tiers.lockPeriodHelper' })}
          label={formatMessage({ id: 'admin.tiers.lockPeriod' })}
          max={365}
          min={1}
          step={1}
          type="number"
          value={lockDays}
          onChange={(e) => setLockDays(e.target.value)}
        />
        <TextareaField
          label={formatMessage({ id: 'admin.tiers.description' })}
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </AppDialog.Body>
      <AppDialog.Footer>
        <Button
          disabled={isPending}
          onClick={onClose}
        >
          <FormattedMessage id="admin.common.cancel" />
        </Button>
        <Button
          disabled={isPending}
          variant="contained"
          onClick={handleSubmit}
        >
          {isEdit ? (
            <FormattedMessage id="admin.common.save" />
          ) : (
            <FormattedMessage id="admin.common.create" />
          )}
        </Button>
      </AppDialog.Footer>
    </AppDialog>
  )
}
